import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import type { ReactNode } from "react";

import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";

import {
    StyledDashboardCollapse,
    StyledDashboardCollapseList,
    StyledDashboardCollapseListItem,
} from "./Styled";

const isPrimitiveValue = (value: ReactNode): value is string | number =>
    typeof value === "string" || typeof value === "number";

export interface DashboardCollapseListItem<TInfo = never> {
    label: string;
    value: ReactNode;
    info?: TInfo;
}

interface DashboardCollapseProps<TInfo = never> {
    header: ReactNode;
    items: DashboardCollapseListItem<TInfo>[];
    onInfoClick?: (info: TInfo) => void;
}

export function DashboardCollapse<TInfo = never>({
    header,
    items,
    onInfoClick,
}: DashboardCollapseProps<TInfo>) {
    const renderValue = (
        value: DashboardCollapseListItem<TInfo>["value"],
        info?: TInfo,
    ) => {
        const shouldShowInfoIcon = info !== undefined;

        return (
            <Flex gap={12} align="center">
                {isPrimitiveValue(value) ? (
                    <PrimaryText fontWeight="500">{value}</PrimaryText>
                ) : (
                    value
                )}

                {shouldShowInfoIcon && (
                    <Button
                        type="text"
                        icon={<InfoCircleOutlined style={{ fontSize: 24 }} />}
                        onClick={() => onInfoClick?.(info)}
                    />
                )}
            </Flex>
        );
    };

    const renderListItem = ({
        label,
        value,
        info,
    }: DashboardCollapseListItem<TInfo>) => (
        <StyledDashboardCollapseListItem key={`${label}-${String(value)}`}>
            <SecondaryText textNoWrap>{label}</SecondaryText>

            {renderValue(value, info)}
        </StyledDashboardCollapseListItem>
    );

    return (
        <StyledDashboardCollapse
            items={[
                {
                    label: header,
                    children: (
                        <StyledDashboardCollapseList>
                            {items.map(renderListItem)}
                        </StyledDashboardCollapseList>
                    ),
                },
            ]}
        />
    );
}
